/**
 * import-presets.ts — ชุด mapping สำเร็จรูปสำหรับหน้า “นำเข้าและซิงค์” (T097)
 *
 * ใช้ร่วมกัน web/api: web สร้าง UI mapping จาก preset, api ใช้ config เดียวกัน normalize
 * Demo รองรับ financial_entries (ค่าใช้จ่าย/รายได้อื่น) — ช่องทางที่ยังไม่มี API ใช้ไฟล์ผ่าน adapter เดียว
 */

export type PresetFieldType = 'money' | 'date' | 'text' | 'int' | 'externalId';

export interface PresetField {
  /** ชื่อ target ที่ normalize/writer ใช้ (ตรงกับคอลัมน์ปลายทาง) */
  target: string;
  label: string;
  type: PresetFieldType;
  required?: boolean;
  /** คำที่ใช้เดา column ในไฟล์ (ตรงตัวหรือมีคำนี้อยู่) */
  hints: string[];
}

export interface ImportPreset {
  key: string;
  label: string;
  description: string;
  entity: 'financial_entries';
  classification: 'expense' | 'income';
  externalIdField: string;
  coverageDateField: string;
  taxCategoryField?: string;
  fields: PresetField[];
}

const COMMON_FIELDS: PresetField[] = [
  {
    target: 'external_id',
    label: 'เลขที่รายการ (ใช้กันยอดซ้ำ)',
    type: 'externalId',
    required: true,
    hints: ['external_id', 'เลขที่', 'เลขที่รายการ', 'doc_no', 'id', 'ref'],
  },
  {
    target: 'event_date',
    label: 'วันที่เกิดรายการ',
    type: 'date',
    required: true,
    hints: ['event_date', 'วันที่', 'date', 'วันที่เอกสาร'],
  },
  {
    target: 'accounting_date',
    label: 'วันที่ลงบัญชี',
    type: 'date',
    hints: ['accounting_date', 'วันที่ลงบัญชี', 'posting date'],
  },
  {
    target: 'counterparty_name',
    label: 'คู่ค้า',
    type: 'text',
    hints: ['counterparty', 'คู่ค้า', 'ผู้ขาย', 'supplier', 'vendor', 'ชื่อผู้รับเงิน'],
  },
  {
    target: 'counterparty_tax_id',
    label: 'เลขผู้เสียภาษีคู่ค้า',
    type: 'text',
    hints: ['tax_id', 'เลขผู้เสียภาษี', 'เลขประจำตัวผู้เสียภาษี'],
  },
  {
    target: 'invoice_no',
    label: 'เลขใบกำกับ',
    type: 'text',
    hints: ['invoice', 'ใบกำกับ', 'เลขใบกำกับ'],
  },
  {
    target: 'pnl_category',
    label: 'หมวด P&L',
    type: 'text',
    hints: ['category', 'หมวด', 'หมวดบัญชี', 'ประเภทค่าใช้จ่าย'],
  },
  {
    target: 'amount_before_vat',
    label: 'ยอดก่อน VAT',
    type: 'money',
    hints: ['amount_before_vat', 'ก่อน vat', 'ยอดก่อนภาษี', 'subtotal'],
  },
  { target: 'vat_amount', label: 'VAT', type: 'money', hints: ['vat', 'ภาษีมูลค่าเพิ่ม'] },
  {
    target: 'wht_amount',
    label: 'หัก ณ ที่จ่าย',
    type: 'money',
    hints: ['wht', 'หัก ณ ที่จ่าย', 'ภาษีหัก'],
  },
  {
    target: 'total_amount',
    label: 'ยอดรวม',
    type: 'money',
    required: true,
    hints: ['total', 'ยอดรวม', 'จำนวนเงิน', 'amount'],
  },
  {
    target: 'paid_amount',
    label: 'จ่ายจริง',
    type: 'money',
    hints: ['paid', 'จ่ายจริง', 'ชำระแล้ว'],
  },
];

export const IMPORT_PRESETS: ImportPreset[] = [
  {
    key: 'expenses',
    label: 'ค่าใช้จ่าย (ไฟล์รายจ่าย)',
    description: 'นำเข้ารายจ่ายจาก Excel/Google Sheets ที่ export เป็น CSV',
    entity: 'financial_entries',
    classification: 'expense',
    externalIdField: 'external_id',
    coverageDateField: 'event_date',
    taxCategoryField: 'pnl_category',
    fields: COMMON_FIELDS,
  },
  {
    key: 'other_income',
    label: 'รายได้อื่น',
    description: 'ค่าบริหารการตลาด ขายวัสดุแพ็ก เงินชดเชย ฯลฯ',
    entity: 'financial_entries',
    classification: 'income',
    externalIdField: 'external_id',
    coverageDateField: 'event_date',
    fields: COMMON_FIELDS,
  },
];

export const presetByKey = (key: string): ImportPreset | undefined =>
  IMPORT_PRESETS.find((p) => p.key === key);

/**
 * เดา column ในไฟล์ให้แต่ละ target (ไม่สนตัวพิมพ์/ช่องว่าง/ขีด)
 * - รอบแรกจับคู่แบบตรงตัวก่อน แล้วค่อยจับแบบ "มีคำนี้อยู่"
 *   (กัน "ยอดก่อน VAT" ไปโดนช่อง VAT)
 * - หนึ่งคอลัมน์ใช้ได้กับ target เดียว
 */
export function guessMapping(preset: ImportPreset, headers: string[]): Record<string, string> {
  const norm = (s: string): string => s.toLowerCase().replace(/[\s_-]/g, '');
  const mapping: Record<string, string> = {};
  const used = new Set<string>();

  const claim = (target: string, header: string): void => {
    mapping[target] = header;
    used.add(header);
  };

  for (const field of preset.fields) {
    const exact = headers.find(
      (h) => !used.has(h) && field.hints.some((hint) => norm(h) === norm(hint)),
    );
    if (exact) claim(field.target, exact);
  }
  for (const field of preset.fields) {
    if (mapping[field.target]) continue;
    const partial = headers.find(
      (h) => !used.has(h) && field.hints.some((hint) => norm(h).includes(norm(hint))),
    );
    if (partial) claim(field.target, partial);
  }
  return mapping;
}

/** แปลง preset + mapping → config ที่ API ใช้ normalize (ส่งเฉพาะ field ที่ map แล้ว) */
export function presetToConfig(
  preset: ImportPreset,
  mapping: Record<string, string>,
  expectedYear?: number,
): {
  entity: 'financial_entries';
  externalIdField: string;
  coverageDateField: string;
  expectedYear?: number;
  taxCategoryField?: string;
  fields: { target: string; type: PresetFieldType; required?: boolean }[];
} {
  return {
    entity: preset.entity,
    externalIdField: preset.externalIdField,
    coverageDateField: preset.coverageDateField,
    ...(expectedYear !== undefined ? { expectedYear } : {}),
    ...(preset.taxCategoryField ? { taxCategoryField: preset.taxCategoryField } : {}),
    fields: preset.fields
      .filter((f) => mapping[f.target] !== undefined || f.required)
      .map((f) => ({
        target: f.target,
        type: f.type,
        ...(f.required ? { required: true } : {}),
      })),
  };
}
